// @core
import PropTypes from 'prop-types';
import { i18n } from 'core/i18n';

// @components
import Wizard from 'components/commons/wizard/wizard';
import WizardStep from 'components/commons/wizardStep/wizardStep';
import { OKButton } from 'components/commons/OKButton/OKButton'
import { SettingTransferStep } from '../settingTransferStep/settingTransferStep';
import { SettingResumeStep } from '../settingResumeStep/settingResumeStep';
import { TransferAuthorizationStep } from '../transferAuthorizationStep/transferAuthorizationStep';
import { TransferSummaryStep } from '../transferSummaryStep/transferSummaryStep';

// @helpers
import { validateByStep } from '../../helpers/bringAccountHelper';
import { getTaggingWizard, taggingFinishProcess, taggingWizardAction } from '../../helpers/bringAccountTagging';

// @contants
import { WIZARD_FOURTH_STEP } from 'constants';
import { WIZARD_BUTTON_CANCEL, WIZARD_BUTTON_NEXT, WIZARD_BUTTON_PREVIOUS } from '../../constants/bringAccountConstantsTagging';
import { AUTHORISE_REQUEST, AUTHORISE_STEPS, STATUSES } from '../../constants/bringAccountConstants';

import './bringAccountWizard.scss';

const { FormattedText } = i18n;

const FinalButton = ({ onClick, isLoading }) => (
    <div className="wizard-step__fourth-button">
        <OKButton onClick={onClick} disabled={isLoading} loading={isLoading}>
            <FormattedText value="transfer_account-transfer_account_from_another_bank_step4_send_button" />
        </OKButton>
    </div>
)

export const BringAccountWizard = ({ immBringAccount, immProfile, immAccount, isOffice, history, actions }) => {
    const { setVisibleStep, validateStep, ...flowSteps} = actions;
    const isDisabledNextButton = !validateByStep(immBringAccount, { currentStep: immBringAccount.get('currentStep') });
    const isSendingTransferRequest = immBringAccount.get('status') === STATUSES.SAVING;

    const wizardActions = {
        setVisibleStep: actions.setVisibleStep,
        validateStep: actions.validateStep,
        cancel: () => {
            history.push('/myprofile/accounts');
        },
        setWillCancel: () => { }
    }

    const handleFinishTransferRequest = (immBringAccount) => {
        const bringAccountData = immBringAccount.toJS();

        taggingFinishProcess()
        actions.sendTransferRequest({
            originAccountNumber: bringAccountData.iban.number,
            destinationAccountNumber: immAccount.get('iban'),
            targetDate: bringAccountData.executionDate,
            requestsOriginBank1: bringAccountData[AUTHORISE_REQUEST],
            requestsOriginBank2: bringAccountData[AUTHORISE_STEPS]
        })
    }

    return (
        <Wizard
            actions={wizardActions}
            immSteps={immBringAccount.get('steps')}
            visibleStep={immBringAccount.get('currentStep')}
            stepsAmount={WIZARD_FOURTH_STEP}
            wizardClass="transfer-account__wizard"
            hasSignaturePattern={false}
            isTaggingSecondVersion
            tealiumEvents={{
                [WIZARD_BUTTON_CANCEL]: taggingWizardAction(WIZARD_BUTTON_CANCEL),
                [WIZARD_BUTTON_NEXT]: taggingWizardAction(WIZARD_BUTTON_NEXT),
                [WIZARD_BUTTON_PREVIOUS]: taggingWizardAction(WIZARD_BUTTON_PREVIOUS)
            }}
            tealium={getTaggingWizard(immAccount, immBringAccount)}
        >
            <WizardStep
                key="settingTransferStep"
                position="first"
                nextButtonDisabled={isDisabledNextButton}
                cancelButton
            >
                <SettingTransferStep immBringAccount={immBringAccount} immProfile={immProfile} isOffice={isOffice} actions={flowSteps} />
            </WizardStep>
            <WizardStep
                key="settingResumeStep"
                position="middle"
                nextButtonDisabled={isDisabledNextButton}
                cancelButton
            >
                <SettingResumeStep immAccount={immAccount} isOffice={isOffice} />
            </WizardStep>
            <WizardStep
                key="transferAuthorizationStep"
                position="middle"
                nextButtonDisabled={isDisabledNextButton}
                cancelButton
            >
                <TransferAuthorizationStep immBringAccount={immBringAccount} isOffice={isOffice} actions={flowSteps} />
            </WizardStep>
            <WizardStep
                key="transferSummaryStep"
                cancelButton
                position="last"
                omitPreviousButtonWP
                overrides={{
                    FinalButton: (
                        <FinalButton
                            onClick={() => handleFinishTransferRequest(immBringAccount)}
                            isLoading={isSendingTransferRequest}
                        />
                    )
                }}
            >
                <TransferSummaryStep immAccount={immAccount} isOffice={isOffice} immBringAccount={immBringAccount} />
            </WizardStep>
        </Wizard>
    )
}

BringAccountWizard.propTypes = {
    immBringAccount: PropTypes.object.isRequired,
    immProfile: PropTypes.object.isRequired,
    immAccount: PropTypes.object.isRequired,
    actions: PropTypes.shape({
        setVisibleStep: PropTypes.func.isRequired,
        validateStep: PropTypes.func.isRequired,
        sendTransferRequest: PropTypes.func.isRequired,
        setIban: PropTypes.func.isRequired,
        setExecutionDate: PropTypes.func.isRequired,
        updateAuthoriseCheckbox: PropTypes.func.isRequired,
        getEntityName: PropTypes.func.isRequired,
    }).isRequired,
    history: PropTypes.object.isRequired,
    isOffice: PropTypes.bool.isRequired,
}
